// 🙋 "Wasn't played?" — the two-step sheet behind the quiet link on a fan's
// played card (founder, 2026-10-09). Step 1 says what happens (we check the
// talent's set list) on the green Dispute Charge slab; step 2 is the
// founder's "Are you sure?" with the consequence — a claim the set list
// disproves blocks this fan for good — and the red confirm. The tap goes to
// the server; the card re-renders from the claim it answers with
// ("Checking with {djName}").
//
// Drunk-proof: one bold line per step, big slabs, Cancel always there. The
// look is FanBoostSheet's (fbs-*), portaled to <body> for the same reason:
// the My Requests tab scrolls inside a fixed container that traps z-index.

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { claimsApi } from "../services/claims";
import { ordersApi } from "../services/orders";
import type { FanOrderRow } from "../lib/fanRequestsModel";
import "./FanBoostSheet.css";
import "./ClaimSheet.css";
import { t } from "../i18n";

// ⚠ drawn, never an emoji (standing rule): triangle, bar, dot.
const WARN = "M12 3.6 2.4 20.4h19.2L12 3.6ZM12 9.8v5M12 17.6h.01";

type Props = {
  row: FanOrderRow;
  /** The fan's total in their currency — the card's own "Requested for" amount. */
  amount: string;
  /** Fresh server truth for this order (FanLiveCard's onUpdate). */
  onUpdate: (patch: Partial<FanOrderRow> & { orderId: string }) => void;
  onClose: () => void;
};

const ClaimSheet: React.FC<Props> = ({ row, amount, onUpdate, onClose }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const djName = row.djName || "DJ";

  const submit = async () => {
    if (busy || blocked) return;
    setBusy(true);
    setError(null);
    try {
      const res = await claimsApi.open(row.orderId);
      onUpdate({ orderId: row.orderId, claimable: false, claim: res.claim });
      onClose();
      return;
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        // Blocked (a set list already proved this fan wrong — for good) or
        // too_many (a 3rd claim in 30 days): the same answer for both.
        setBlocked(true);
        setError(t("claimSheet.youCanTAskAgain"));
        onUpdate({ orderId: row.orderId, claimable: false });
        setBusy(false);
        return;
      }
      if (status === 409) {
        // Already claimed (another tab or phone) or no longer claimable: the
        // order moved under the fan. Show where it is instead of an error.
        try {
          const fresh = await ordersApi.status(row.orderId);
          onUpdate({ ...fresh, orderId: row.orderId });
          if (fresh.claim || fresh.claimable !== true) {
            onClose();
            return;
          }
        } catch {
          /* offline — the retry line below */
        }
      }
      setError(t("claimSheet.couldnTSendThat"));
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fbs-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="fbs-card cls-card" role="dialog" aria-modal="true" aria-label={t("claimSheet.songWasnTPlayed")}>
        <div className="fbs-grab" aria-hidden="true" />
        {step === 1 ? (
          <>
            <div className="cls-art">
              {row.trackImage ? <img src={row.trackImage} alt="" /> : <div className="cls-art-ph" aria-hidden="true">♪</div>}
            </div>
            <h3 className="fbs-title">{t("claimSheet.songWasnTPlayed")}</h3>
            <div className="cls-big">{t("claimSheet.weLlCheckSetList", { djName })}</div>
            <button type="button" className="fbs-slab fbs-slab--green" onClick={() => setStep(2)}>
              <span className="fbs-l1wrap">
                <svg className="fbs-bolt" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d={WARN} />
                </svg>
                <span className="fbs-l1">{t("claimSheet.disputeCharge")}</span>
              </span>
              <span className="fbs-l2">{t("claimSheet.verifyMySongWas", { amount })}</span>
            </button>
          </>
        ) : (
          <>
            <h3 className="fbs-title">{t("claimSheet.areYouSure")}</h3>
            <p className="cls-sure">{t("claimSheet.weAreGoingTo", { djName })}</p>
            {!blocked && (
              <button type="button" className="fbs-slab cls-slab--red" onClick={submit} disabled={busy}>
                <span className="fbs-l1">{t("claimSheet.yesItWasNot")}</span>
              </button>
            )}
          </>
        )}

        {error && <div className="fbs-error">{error}</div>}

        <button type="button" className="fbs-skip" onClick={onClose} disabled={busy}>
          {t("claimSheet.cancel")}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default ClaimSheet;
